/**
 * API client utilities.
 * Provides typed functions for calling Homebase API endpoints.
 */

// Determine API base URL based on environment
// In production/staging, use same-domain URLs (proxied via Pages Functions)
// In development, call the Worker directly at localhost
const API_BASE_URL =
  import.meta.env.MODE === 'development'
    ? 'http://localhost:8787'
    : '';

/**
 * Generic fetch wrapper with JSON parsing
 */
async function fetchAPI<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  // In development, add mock user header
  if (import.meta.env.MODE === 'development') {
    headers['X-Mock-User'] = 'hello@fastail.ai';
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Health check
export async function fetchHealth(): Promise<{
  status: string;
  environment: string;
  checks: Record<string, { status: string; message?: string }>;
  timestamp: string;
}> {
  return fetchAPI('/api/health');
}

// Current user
export async function fetchCurrentUser(): Promise<{
  userId: string;
  email: string;
  name: string;
  role: 'admin' | 'member';
}> {
  const data = await fetchAPI<{ user: any }>('/api/users/me');
  return data.user;
}

// Users list
export async function fetchUsers(): Promise<
  Array<{
    id: string;
    email: string;
    name: string;
    role: string;
  }>
> {
  const data = await fetchAPI<{ users: any[] }>('/api/users');
  return data.users;
}

// Modules
export async function fetchModules(): Promise<
  Array<{
    id: string;
    slug: string;
    name: string;
    version: string;
    status: string;
    config: Record<string, unknown> | null;
  }>
> {
  const data = await fetchAPI<{ modules: any[] }>('/api/modules');
  return data.modules;
}

export async function fetchModule(slug: string): Promise<{
  id: string;
  slug: string;
  name: string;
  version: string;
  status: string;
  config: Record<string, unknown> | null;
}> {
  const data = await fetchAPI<{ module: any }>(`/api/modules/${slug}`);
  return data.module;
}

export async function updateModule(
  slug: string,
  updates: { status?: string; config?: Record<string, unknown> }
): Promise<{
  id: string;
  slug: string;
  name: string;
  version: string;
  status: string;
  config: Record<string, unknown> | null;
}> {
  const data = await fetchAPI<{ module: any }>(`/api/modules/${slug}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  return data.module;
}

// Export
export async function exportD1(): Promise<{
  status: string;
  exportId: string;
  filePath: string;
  fileSize: number;
}> {
  return fetchAPI('/api/export/d1', {
    method: 'POST',
  });
}

// ---------- CashFlow module ----------

import type {
  MonthlySummary,
  Asset,
  Liability,
  QuickAddEntry,
  MonthAnnotation,
} from '../types/cashflow';

interface ServerQuickAdd {
  id: string;
  type: 'income' | 'asset' | 'liability';
  name: string;
  amount: number;
  entry_date: string;
  note: string | null;
  category: string | null;
  monthly_cashflow: number | null;
  created_by: string;
  created_at: string;
}

interface ServerAnnotation {
  month: string;
  note: string;
  updated_by: string;
  updated_at: string;
}

function mapServerQuickAdd(q: ServerQuickAdd): QuickAddEntry {
  return {
    id: q.id,
    type: q.type,
    name: q.name,
    amount: q.amount,
    date: q.entry_date,
    note: q.note ?? undefined,
    category: q.category ?? undefined,
    monthly_cashflow: q.monthly_cashflow ?? undefined,
  };
}

function mapServerAnnotation(a: ServerAnnotation): MonthAnnotation {
  return { month: a.month, note: a.note };
}

export async function fetchCashflowSummaries(): Promise<MonthlySummary[]> {
  const data = await fetchAPI<{ summaries: MonthlySummary[] }>('/api/m/cashflow/summaries');
  return data.summaries;
}

export async function fetchCashflowAssets(): Promise<Asset[]> {
  const data = await fetchAPI<{ assets: Asset[] }>('/api/m/cashflow/assets');
  return data.assets;
}

export async function fetchCashflowLiabilities(): Promise<Liability[]> {
  const data = await fetchAPI<{ liabilities: Liability[] }>('/api/m/cashflow/liabilities');
  return data.liabilities;
}

export async function fetchCashflowQuickAdds(): Promise<QuickAddEntry[]> {
  const data = await fetchAPI<{ quickAdds: ServerQuickAdd[] }>('/api/m/cashflow/quick-adds');
  return data.quickAdds.map(mapServerQuickAdd);
}

export async function fetchCashflowAnnotations(): Promise<MonthAnnotation[]> {
  const data = await fetchAPI<{ annotations: ServerAnnotation[] }>('/api/m/cashflow/annotations');
  return data.annotations.map(mapServerAnnotation);
}

export async function postCashflowQuickAdd(entry: QuickAddEntry): Promise<QuickAddEntry> {
  const data = await fetchAPI<{ quickAdd: ServerQuickAdd }>('/api/m/cashflow/quick-adds', {
    method: 'POST',
    body: JSON.stringify({
      type: entry.type,
      name: entry.name,
      amount: entry.amount,
      entry_date: entry.date,
      note: entry.note,
      category: entry.category,
      monthly_cashflow: entry.monthly_cashflow,
    }),
  });
  return mapServerQuickAdd(data.quickAdd);
}

export async function putCashflowAnnotation(month: string, note: string): Promise<MonthAnnotation> {
  const data = await fetchAPI<{ annotation: ServerAnnotation }>(
    `/api/m/cashflow/annotations/${month}`,
    {
      method: 'PUT',
      body: JSON.stringify({ note }),
    }
  );
  return mapServerAnnotation(data.annotation);
}

// ---------- Admin settings ----------

export interface AdminSetting {
  key: string;
  value: unknown;
  label: string | null;
  updated_by: string;
  updated_at: string;
}

export async function fetchAdminSettings(): Promise<AdminSetting[]> {
  const data = await fetchAPI<{ settings: AdminSetting[] }>('/api/admin/settings');
  return data.settings;
}

export async function fetchAdminSetting(key: string): Promise<AdminSetting> {
  const data = await fetchAPI<{ setting: AdminSetting }>(`/api/admin/settings/${key}`);
  return data.setting;
}

export async function putAdminSetting(
  key: string,
  value: unknown,
  label?: string | null
): Promise<AdminSetting> {
  const data = await fetchAPI<{ setting: AdminSetting }>(`/api/admin/settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value, label }),
  });
  return data.setting;
}

export async function deleteAdminSetting(key: string): Promise<void> {
  await fetchAPI(`/api/admin/settings/${key}`, { method: 'DELETE' });
}

// ---------- Sync jobs ----------

export interface SyncJob {
  id: string;
  route: string;
  module_slug: string | null;
  status: 'pending' | 'running' | 'success' | 'error' | 'timeout';
  initiated_by: string;
  request_payload: Record<string, unknown> | null;
  response_payload: Record<string, unknown> | null;
  validation_errors: string[] | null;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
}

export interface SheetSyncJobRef {
  sheet_id: string;
  job_id: string;
  status: 'running' | 'error';
  error?: string;
}

export async function postCashflowSync(): Promise<{ jobs: SheetSyncJobRef[] }> {
  return fetchAPI('/api/m/cashflow/sync', { method: 'POST' });
}

export async function fetchSyncJob(id: string): Promise<SyncJob> {
  const data = await fetchAPI<{ job: SyncJob }>(`/api/sync-jobs/${id}`);
  return data.job;
}

export async function fetchSyncJobs(limit = 20): Promise<SyncJob[]> {
  const data = await fetchAPI<{ jobs: SyncJob[] }>(`/api/sync-jobs?limit=${limit}`);
  return data.jobs;
}

// ---------- Tracked Google Sheets ----------

export interface TrackedSheet {
  id: string;
  module_slug: string;
  label: string;
  sheet_url: string;
  google_account_email: string | null;
  notes: string | null;
  last_synced_at: string | null;
  last_sync_status: 'pending' | 'running' | 'success' | 'error' | 'timeout' | null;
  last_sync_job_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export async function fetchTrackedSheets(moduleSlug?: string): Promise<TrackedSheet[]> {
  const qs = moduleSlug ? `?module_slug=${encodeURIComponent(moduleSlug)}` : '';
  const data = await fetchAPI<{ sheets: TrackedSheet[] }>(`/api/tracked-sheets${qs}`);
  return data.sheets;
}

export async function createTrackedSheet(input: {
  module_slug: string;
  label: string;
  sheet_url: string;
  google_account_email?: string;
  notes?: string;
}): Promise<TrackedSheet> {
  const data = await fetchAPI<{ sheet: TrackedSheet }>(`/api/tracked-sheets`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return data.sheet;
}

export async function updateTrackedSheet(
  id: string,
  patch: Partial<{
    label: string;
    sheet_url: string;
    google_account_email: string | null;
    notes: string | null;
  }>,
): Promise<TrackedSheet> {
  const data = await fetchAPI<{ sheet: TrackedSheet }>(`/api/tracked-sheets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  return data.sheet;
}

export async function deleteTrackedSheet(id: string): Promise<void> {
  await fetchAPI(`/api/tracked-sheets/${id}`, { method: 'DELETE' });
}

export async function syncTrackedSheet(id: string): Promise<{
  sheet_id: string;
  job_id: string;
  status: 'running' | 'error';
  error?: string;
}> {
  return fetchAPI(`/api/tracked-sheets/${id}/sync`, { method: 'POST' });
}

// ---------- (cashflow bulk continues below) ----------

export async function postCashflowBulk(payload: {
  source: 'csv' | 'sync' | 'demo';
  summaries?: MonthlySummary[];
  assets?: Asset[];
  liabilities?: Liability[];
  replace?: { summaries?: boolean; assets?: boolean; liabilities?: boolean };
}): Promise<{ status: string; written: { summaries: number; assets: number; liabilities: number } }> {
  return fetchAPI('/api/m/cashflow/bulk', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
